<script lang="ts">

/** Значение, которое Vue принимает в атрибут class */
export type ClassValue = string | Record<string, boolean> | Array<string | Record<string, boolean>>;

/** Конфигурация колонки (парсится из VDataGridColumnV2) */
export type DataGridColumn = {
  id: string;
  label: string;
  width: string | null;
  contentAlign: 'left' | 'center' | 'right';
  contentClass: ClassValue | ((row: Record<string, unknown>) => ClassValue) | null;
  headerClass: ClassValue | null;

  sortable: boolean;
  sortDefaultValue: string | null,

  hint: string | null;
  hintIconHide: boolean;

  filterable: boolean;
  filterType: 'text' | 'select' | 'date-range' | 'number' | 'mask';
  filterProps: Record<string, unknown>;
  filterDefaultValue: unknown;
  filterSendName: string | null;
  filterIdentity: string | string[] | null;

  labelSlot: (() => VNode[]) | null;
  /**
   * Стабильный компонент label-слота. Создаётся один раз в parseColumn
   * (markRaw({ render: labelSlot })). Инлайновый { render } в шаблоне грида
   * пересоздавался на каждом ререндере — Vue считал объект новым компонентом
   * и перемонтировал DOM заголовка, ломая сохранённые ссылки на элементы
   * (якоря попапов меню шапки).
   */
  labelComponent: Component | null;
  cellSlot: ((props: { row: Record<string, unknown>; value: unknown }) => VNode[]) | null;
  footerSlot: (() => VNode[]) | null;

  /**
   * Обработчик редактирования ячейки.
   * Выставляется автоматически, если на колонке висит @edit:
   *   <VDataGridColumnV2 @edit="(row, anchor) => ..." />
   * Грид рисует карандаш в углу ячейки и перехватывает одиночный клик
   * по ячейке (с stopPropagation, чтобы не сработал @row:dblclick).
   * anchor — DOM-узел самой ячейки (<td>), удобен для якорения попапа.
   */
  onEdit: ((row: Record<string, unknown>, anchor: HTMLElement) => void) | null;

  /**
   * Material-имя иконки, которой помечается редактируемая ячейка.
   * По умолчанию 'edit' (карандаш). Имеет смысл только вместе с @edit.
   */
  editMaterial: string;
};

/**
 * VDataGridColumnV2 — декларативный компонент колонки
 *
 * Грид парсит его из слота, сам компонент ничего не рендерит.
 *
 * @example
 * <VDataGridColumnV2
 *   id="name"
 *   label="Название"
 *   filterable
 *   filter-type="text"
 *   sortable
 *   width="200px"
 * >
 *   {{ row.name }}
 * </VDataGridColumnV2>
 */
export default defineComponent({
  name: 'VDataGridColumnV2',

  props: {
    /**
     * Идентификатор колонки (соответствует полю в данных)
     */
    id: {
      type: String as PropType<string>,
      required: true,
    },

    /**
     * Текст заголовка
     */
    label: {
      type: String as PropType<string>,
      default: '',
    },

    /**
     * Ширина колонки (CSS значение)
     */
    width: {
      type: String as PropType<string>,
      default: null,
    },

    /**
     * Выравнивание контента
     */
    contentAlign: {
      type: String as PropType<'left' | 'center' | 'right'>,
      default: 'left',
      validator: (v: string) => ['left', 'center', 'right'].includes(v),
    },

    /**
     * CSS-класс(ы) для ячеек контента (<td>).
     *
     * Поддерживает четыре формата:
     * - строка: content-class="col-amount tabular"
     * - объект: :content-class="{ error: hasError }"
     * - массив: :content-class="['ellipsis', { error: hasError }]"
     * - функция от строки: :content-class="(row) => ({ canceled: row.isCanceled })"
     *
     * Функция вызывается на каждый рендер ячейки и получает данные строки.
     * Не применяется к <th> и ячейкам футера.
     */
    contentClass: {
      type: [String, Array, Object, Function] as PropType<DataGridColumn['contentClass']>,
      default: null,
    },

    /**
     * CSS-класс(ы) для заголовка колонки (<th>).
     *
     * Типовое использование — header-class="nowrap" чтобы заголовок не переносился.
     * Также поддерживает объект и массив (см. contentClass).
     * Функцию не принимает — у заголовка нет данных строки, от которых зависеть.
     */
    headerClass: {
      type: [String, Array, Object] as PropType<ClassValue>,
      default: null,
    },

    /**
     * Включить сортировку
     */
    sortable: {
      type: Boolean,
      default: false,
    },

    /**
     * Включить фильтрацию
     */
    filterable: {
      type: Boolean,
      default: false,
    },

    /**
     * Тип фильтра: text, select, date-range, number, mask
     */
    filterType: {
      type: String as PropType<'text' | 'select' | 'date-range' | 'number' | 'mask'>,
      default: 'text',
      validator: (v: string) => ['text', 'select', 'date-range', 'number', 'mask'].includes(v),
    },

    /**
     * Пропсы для компонента фильтра
     */
    filterProps: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },

    /**
     * Значение фильтра по умолчанию
     */
    filterDefaultValue: {
      type: [String, Number, Object, Array] as PropType<unknown>,
      default: null,
    },

    /**
     * Название поля для отправки на сервер
     */
    filterSendName: {
      type: String as PropType<string>,
      default: null,
    },

    /**
     * Поле(я) объекта опции, которые будут отправлены на сервер.
     * Если указано несколько через запятую — каждое поле отправляется отдельным параметром в корень payload.
     * Примеры: filter-identity="isVerified" или filter-identity="isShipped,isShippedEntirely"
     * Не передаётся в VSelect — используется только внутри VDataGridV2.
     */
    filterIdentity: {
      type: String as PropType<string>,
      default: null,
    },

    /** начальное значение сортировки таблицы **/
    sortDefaultValue: {
      type:  String as PropType<'desc' | 'asc'>,
      default: null,
    },

    /**
     * Поясняющая подсказка к заголовку колонки.
     * Показывается тултипом при наведении на шапку — раскрывает смысл столбца.
     */
    hint: {
      type:    String as PropType<string>,
      default: null,
    },

    /**
     * Нужно ли скрывать иконку поясняющей подсказки
     */
    hintIconHide: {
      type: Boolean,
      default: false,
    },

    /**
     * Иконка (material) в углу ячейки для колонок с @edit.
     * Позволяет заменить карандаш на смысловую иконку: 'visibility', 'info', 'link'.
     */
    editMaterial: {
      type:    String as PropType<string>,
      default: 'edit',
    },
  },

  render() {
    return null;
  },
});
</script>
